/**
 * 도움말 상태기계 — **순수 함수.** LLM 보다 먼저 사용자 말을 받아 메뉴·주제 카드·Quick Start 를
 * 정해진 순서로 낸다. 설계는 datamodeler `docs/features/ai-chat/ai-help-agent.md` 를 옮긴 것.
 *
 * 왜 LLM 이 아니라 상태기계인가: 사용법을 LLM 이 한꺼번에 쏟아내면 읽히지 않는다. 메뉴 → 선택 →
 * 단계 진행은 빠르고 항상 같은 흐름이어야 한다. LLM 은 그 바깥의 자유 질문과 "더 자세히" 만 맡는다.
 *
 * 「대신 해줘」는 LLM 의 판단이 아니라 **미리 정해 둔 동작**이다 — 매번 같은 결과라야 "따라 하기" 가
 * 성립한다. 동작은 실제 작업 순서 그대로다: 연결 → 세션 열기 → 새 탭 → SQL 넣기 → 실행 → 실행계획.
 * 예시 SQL 은 어느 연결에서든 도는 카탈로그 질의뿐이다.
 *
 * 반환값:
 *   handled   false 면 이 모듈이 상관할 말이 아니다 — 평소처럼 LLM 으로
 *   messages  화면에 추가할 안내 [{ text, options? }]. text 는 마크다운(```sql 은 실행 버튼이 달린다)
 *   state     다음 상태 (null 이면 종료)
 *   actions   화면이 수행할 동작 [{ type: 'open_connections'|'open_session'|'new_tab'|'insert_sql'|'run_sql'|'explain_sql', sql? }]
 *   agentText 있으면 **상태를 유지한 채** 이 힌트와 함께 사용자 원문을 LLM 에 보낸다
 *
 * 문구는 전부 t() 로 받는다 — 버튼 라벨 비교도 t() 결과와 한다.
 */

/** "도움말/사용법/quick start…" — 어느 상태에서든 처음부터. */
const TRIGGER = /(도움말|사용법|사용 방법|튜토리얼|quick\s*start|퀵\s*스타트|^help$|how to use)/i;
/** 주제를 콕 집은 질문의 단서 — 이게 없으면 "실행계획 보여줘" 같은 작업 요청과 구분되지 않는다. */
const HOWTO_CUE = /(어떻게|어디서|방법|사용법|뭐야|무엇|뭔가요|어떤 건|알려줘|설명)/;

export function isHelpTrigger(text) {
  return TRIGGER.test(String(text || '').trim());
}

/** 주제 제목이 들어 있고 사용법 단서가 있으면 그 주제. 제목이 긴 것부터 본다 ("SQL 실행" 이 "실행" 보다 먼저). */
export function matchTopic(text, topics) {
  const s = String(text || '');
  if (!HOWTO_CUE.test(s)) return null;
  const sorted = [...(topics || [])].sort((a, b) => b.title.length - a.title.length);
  return sorted.find(tp => s.includes(tp.title)) || null;
}

const btn = (t, k) => t(`assistant:help.btn.${k}`);

function menuMessage(t) {
  return { text: t('assistant:help.menu'), options: [btn(t, 'quickStart'), btn(t, 'topics'), btn(t, 'free')] };
}

function topicsMessage(t, topics) {
  return { text: t('assistant:help.pickTopic'), options: topics.map(tp => tp.title) };
}

function topicCard(t, tp) {
  const lines = [`**${tp.title}** — ${tp.summary}`, ''];
  tp.steps.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
  if (tp.sampleSql) lines.push('', t('assistant:help.sampleIntro'), '```sql', tp.sampleSql, '```');
  return { text: lines.join('\n'), options: [btn(t, 'more'), btn(t, 'otherTopic'), btn(t, 'stop')] };
}

/**
 * Quick Start 단계 — **실제로 SQL 을 한 번 돌려 보기까지의 순서** 그대로.
 * 실습 단계는 `doActions` 가 있고 [해봤어요, 다음] [대신 해줘] 가 붙는다. 이미 돼 있는 단계(연결·세션)는
 * 그 사실을 말하고 [다음] 만 보인다 — 있는 것을 또 만들게 하지 않는다.
 */
export function quickStartSteps(t, { sample, connectionName, hasSession }) {
  const withSql = (key) => `${t(`assistant:help.qs.${key}`)}\n\n\`\`\`sql\n${sample}\n\`\`\``;
  return [
    connectionName
      ? { title: t('assistant:help.qs.connectTitle'), text: t('assistant:help.qs.connectHave', { name: connectionName }) }
      : {
        title: t('assistant:help.qs.connectTitle'),
        text: t('assistant:help.qs.connect'),
        doActions: [{ type: 'open_connections' }],
        doneText: t('assistant:help.qs.connectDone'),
      },
    hasSession
      ? { title: t('assistant:help.qs.sessionTitle'), text: t('assistant:help.qs.sessionHave') }
      : {
        title: t('assistant:help.qs.sessionTitle'),
        text: t('assistant:help.qs.session'),
        doActions: [{ type: 'open_session' }],
        doneText: t('assistant:help.qs.sessionDone'),
      },
    {
      title: t('assistant:help.qs.tabTitle'),
      text: t('assistant:help.qs.tab'),
      doActions: [{ type: 'new_tab' }],
      doneText: t('assistant:help.qs.tabDone'),
    },
    {
      title: t('assistant:help.qs.insertTitle'),
      text: withSql('insert'),
      doActions: [{ type: 'insert_sql', sql: sample }],
      doneText: t('assistant:help.qs.insertDone'),
    },
    {
      title: t('assistant:help.qs.runTitle'),
      text: t('assistant:help.qs.run'),
      doActions: [{ type: 'run_sql', sql: sample }],
      doneText: t('assistant:help.qs.runDone'),
    },
    { title: t('assistant:help.qs.resultTitle'), text: t('assistant:help.qs.result') },
    {
      title: t('assistant:help.qs.planTitle'),
      text: t('assistant:help.qs.plan'),
      doActions: [{ type: 'explain_sql', sql: sample }],
      doneText: t('assistant:help.qs.planDone'),
    },
    { title: t('assistant:help.qs.saveTitle'), text: t('assistant:help.qs.save'), last: true },
  ];
}
function stepMessage(t, steps, i) {
  const st = steps[i];
  const head = t('assistant:help.qs.stepHead', { n: i + 1, total: steps.length, title: st.title });
  const options = st.last
    ? [btn(t, 'topics'), btn(t, 'free'), btn(t, 'stop')]
    : (st.doActions ? [btn(t, 'didIt'), btn(t, 'doIt'), btn(t, 'stop')] : [btn(t, 'next'), btn(t, 'stop')]);
  return { text: `${head}\n\n${st.text}`, options };
}

/**
 * @param {object|null} state  { mode:'menu' } | { mode:'topics' } | { mode:'topic', id } | { mode:'quick', step }
 * @param {string} text        사용자가 보낸 말 (버튼을 눌렀으면 그 라벨)
 * @param {object} env         { t, topics, sample, connectionName, hasSession }
 */
export function stepHelp(state, text, env) {
  const { t, topics = [], sample, connectionName, hasSession } = env;
  const s = String(text || '').trim();
  const none = { handled: false };
  const is = (k) => s === btn(t, k);

  // 버튼을 눌렀는지 먼저 본다. "Quick Start" 라벨은 트리거 정규식에도 걸리므로, 트리거를 먼저 보면
  // 버튼을 누를 때마다 메뉴로 되돌아간다(테스트에서 실제로 잡혔다).
  const pressed = !!state && ['stop', 'quickStart', 'topics', 'otherTopic', 'free', 'more', 'next', 'didIt', 'doIt'].some(is);

  // 어느 상태든 — 그만 / 처음부터
  if (state && is('stop')) return { handled: true, messages: [{ text: t('assistant:help.bye') }], state: null };
  if (!pressed && isHelpTrigger(s)) return { handled: true, messages: [menuMessage(t)], state: { mode: 'menu' } };

  // 상태 없음 — 주제를 콕 집은 질문만 받는다
  if (!state) {
    const tp = matchTopic(s, topics);
    if (!tp) return none;
    return { handled: true, messages: [topicCard(t, tp)], state: { mode: 'topic', id: tp.id } };
  }

  // 공통 전이 — 주제 목록 / 자유 질문
  if (is('topics') || is('otherTopic')) return { handled: true, messages: [topicsMessage(t, topics)], state: { mode: 'topics' } };
  if (is('free')) return { handled: true, messages: [{ text: t('assistant:help.freeIntro') }], state: null };

  if (state.mode === 'menu') {
    if (is('quickStart')) {
      const steps = quickStartSteps(t, { sample, connectionName, hasSession });
      return { handled: true, messages: [stepMessage(t, steps, 0)], state: { mode: 'quick', step: 0 } };
    }
    // 메뉴에서 다른 말을 하면 자유 질문으로 — 상태를 끊고 LLM 에게
    return { handled: true, messages: [], state: null, agentText: t('assistant:help.hint.fromMenu') };
  }

  if (state.mode === 'topics') {
    const tp = topics.find(x => x.title === s) || matchTopic(s, topics);
    if (tp) return { handled: true, messages: [topicCard(t, tp)], state: { mode: 'topic', id: tp.id } };
    return { handled: true, messages: [], state: null, agentText: t('assistant:help.hint.fromMenu') };
  }

  if (state.mode === 'topic') {
    const tp = topics.find(x => x.id === state.id);
    const title = tp ? tp.title : state.id;
    if (is('more')) {
      return { handled: true, messages: [], state, agentText: t('assistant:help.hint.more', { title, id: state.id }) };
    }
    // 주제 카드에서의 다른 말 — 그 주제 맥락으로 LLM 에게 (상태 유지: 돌아와서 다른 주제를 고를 수 있다)
    return { handled: true, messages: [], state, agentText: t('assistant:help.hint.inTopic', { title, id: state.id }) };
  }

  if (state.mode === 'quick') {
    const steps = quickStartSteps(t, { sample, connectionName, hasSession });
    const i = Math.min(state.step, steps.length - 1);
    const st = steps[i];
    if (is('next') || is('didIt')) {
      const n = i + 1;
      if (n >= steps.length) return { handled: true, messages: [{ text: t('assistant:help.bye') }], state: null };
      return { handled: true, messages: [stepMessage(t, steps, n)], state: { mode: 'quick', step: n } };
    }
    if (is('doIt') && st.doActions) {
      return {
        handled: true,
        messages: [{ text: st.doneText, options: [btn(t, 'next'), btn(t, 'stop')] }],
        state,
        actions: st.doActions,
      };
    }
    // 단계 중의 다른 말 — 단계 맥락으로 LLM 에게, 단계는 유지 (돌아와서 [다음])
    return { handled: true, messages: [], state, agentText: t('assistant:help.hint.inQuick', { n: i + 1, title: st.title }) };
  }

  return none;
}
